import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { formatDistanceStrict } from 'date-fns';
import PropTypes from 'prop-types';
import { historyOrReload } from 'utils/helpers';
import { FIVE_MINUTES_IN_MILLISECONDS } from 'utils/helpers/datetime';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import { discardBooking, touchBooking } from 'redux/actions/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';

const ExpiringModal = ({ show, expiresFrom, reload }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [now, setNow] = useState(new Date());
  const [touched, setTouched] = useState(false);
  const resetBooking = () => {
    dispatch(discardBooking({ clearFerryGrouping: true })).then(() => {
      historyOrReload(history, '/bookings/', reload);
    });
  };

  useEffect(() => {
    if (show) {
      const timer = setInterval(() => {
        setNow(new Date());
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [expiresFrom, show]);

  const touchSession = () => {
    setTouched(true);
    dispatch(touchBooking()).finally(() => {
      setTouched(false);
    });
  };

  const isExpired = new Date(expiresFrom) < new Date();

  return (
    <Modal
      show={show && !touched}
      className="border-0"
      backdropClassName="cover-off-canvas"
      centered
    >
      <div className="p-3">
        <Modal.Header className="pb-3">
          <Modal.Title>
            {isExpired
              ? 'Your booking has expired'
              : 'Your booking is about to expire'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="content px-0 d-flex justify-content-center flex-column text-center">
          {isExpired && (
            <>
              <p>Your booking has expired due to inactivity.</p>
              <Button onClick={resetBooking}>
                Close and return to bookings
              </Button>
            </>
          )}
          {!isExpired && (
            <>
              <p>
                Your booking will expire in{' '}
                {formatDistanceStrict(new Date(expiresFrom), now)}.
              </p>
              <Button onClick={touchSession}>Keep booking active</Button>
            </>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

ExpiringModal.propTypes = {
  show: PropTypes.bool,
  expiresFrom: PropTypes.string,
  reload: PropTypes.bool,
};

const ExpiryBookingCheck = ({ reload = false }) => {
  const BookingStates = Object.freeze({
    Valid: 'valid',
    Expiring: 'expiring',
  });

  const booking = useSelector(getCurrentBooking);
  // Never show a modal if the ContinueEditing modal is being shown.
  const { isEditing, isBookingPayable } = useCurrentBookingState();
  const expiresFrom = booking?.expires_from;
  const [bookingState, setBookingState] = useState(BookingStates.Valid);

  const calculateState = () => {
    const expiresIn = new Date(expiresFrom) - new Date();

    // If the booking contains nothing, dont ever show the expiring popup, just let it timeout.
    if (!isBookingPayable) {
      setBookingState(BookingStates.Valid);
      return;
    }

    if (expiresIn < FIVE_MINUTES_IN_MILLISECONDS) {
      setBookingState(BookingStates.Expiring);
    } else {
      setBookingState(BookingStates.Valid);
    }
  };
  // use this to just force a rerender every 30 seconds to determine if the booking is expired or close to expiry
  useEffect(() => {
    calculateState();

    const checkExpiry = setInterval(() => {
      calculateState();
    }, 30000);

    return () => {
      clearInterval(checkExpiry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.expires_from, isBookingPayable]);

  if (!booking || !expiresFrom) {
    return <></>;
  }

  return (
    <ExpiringModal
      show={!isEditing && bookingState === BookingStates.Expiring}
      expiresFrom={expiresFrom}
      reload={reload}
    />
  );
};

ExpiryBookingCheck.propTypes = {
  reload: PropTypes.bool,
};

export default ExpiryBookingCheck;
